import React, {useCallback, useEffect, useMemo, useState} from "react";
import {AppContext} from "../../HomePage";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {MultiSelect} from "primereact/multiselect";
import {TabPanel, TabView} from "primereact/tabview";
import {Tooltip} from "primereact/tooltip";
import {Loading} from "../../utils/InlineComponents";
import {toDateTimeString} from "../../utils/TimeUtil";
import {requestProbeBaseSpiderOptions, requestProbeProxyOptions, RequestProbeService} from "../../service/RequestProbeService";
import {proxy_countries} from "../../utils/Constants";
import {ScraperService} from "../../service/ScraperService";
import {AiJobService} from "../../service/AiJobService";
import {ProductService} from "../../service/ProductService";
import {formatCurrency, getHostName, getLastPrice, getProductDetailLink} from "../../utils/Utils";
import SiteDefinitionAdd from "./SiteDefinitionAdd";

// Tab header: icon + label. The label is hidden (icon-only) when the panel is
// embedded (e.g. onboarding prepare step) via the rp-tabs-icononly container
// class; a hover tooltip on the icon shows the label in that case.
const iconTabHeader = (iconClass, label) => (
    <span style={{display: "inline-flex", alignItems: "center", gap: ".4em", whiteSpace: "nowrap"}}>
        <i className={iconClass} data-pr-tooltip={label}/><span className="rp-tab-label">{label}</span>
    </span>
);

const RequestProbes = ({initialWebsite, campaignId, embedded} = {}) => {
    const {t, toast, setError} = React.useContext(AppContext);
    const requestProbeService = useMemo(() => new RequestProbeService(), []);
    const scraperService = useMemo(() => new ScraperService(), []);
    const aiJobService = useMemo(() => new AiJobService(), []);
    const productService = useMemo(() => new ProductService(), []);
    const [probeProduct, setProbeProduct] = useState(null);
    const [probeProductLoading, setProbeProductLoading] = useState(false);
    const [inPrepJobs, setInPrepJobs] = useState(null);
    const [inPrepJobsLoading, setInPrepJobsLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [probeDetailLoaded, setProbeDetailLoaded] = useState(false);
    const [scrapingRequestLoading, setScrapingRequestLoading] = useState(false);
    const [scrapingRequestStatus, setScrapingRequestStatus] = useState(null);
    const [filters, setFilters] = useState({request_type: null, website: initialWebsite || "", limit: 50, archive_status: "active"});

    // When embedded (e.g. onboarding Site Definition), focus a specific site.
    // null/undefined -> clear the website filter (show all).
    useEffect(() => {
        const website = initialWebsite || "";
        setFilters(f => (f.website === website ? f : {...f, website: website}));
    }, [initialWebsite]);
    const [selectedProbe, setSelectedProbe] = useState(null);
    const [selectedGroupHash, setSelectedGroupHash] = useState(null);
    const [showFullResponse, setShowFullResponse] = useState(false);
    const [detailTabIndex, setDetailTabIndex] = useState(0);
    const [showAllProductUrls, setShowAllProductUrls] = useState(false);
    const [showAllPageUrls, setShowAllPageUrls] = useState(false);
    const [newProbeUrl, setNewProbeUrl] = useState("");
    const [newProbeRequestType, setNewProbeRequestType] = useState("product_url");
    const [newProbeProxyCountry, setNewProbeProxyCountry] = useState("");
    const [newProbeSelectedProxies, setNewProbeSelectedProxies] = useState([]);
    const [newProbeSelectedBaseSpiders, setNewProbeSelectedBaseSpiders] = useState([]);
    const [newProbeSearchUrlTemplate, setNewProbeSearchUrlTemplate] = useState("");
    const [newProbeSearchKeyword, setNewProbeSearchKeyword] = useState("");
    const [newProbeProductId, setNewProbeProductId] = useState(null);
    const [newProbeCampaignId, setNewProbeCampaignId] = useState(null);
    const [newProbeIsRerun, setNewProbeIsRerun] = useState(false);
    const [newProbeLoading, setNewProbeLoading] = useState(false);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showSiteDefinitionDialog, setShowSiteDefinitionDialog] = useState(false);
    const [siteDefinitionSeed, setSiteDefinitionSeed] = useState(null);
    const [siteDefinitionProbeId, setSiteDefinitionProbeId] = useState(null);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showPromptDialog, setShowPromptDialog] = useState(false);
    const [promptText, setPromptText] = useState("");
    const [spiderDetail, setSpiderDetail] = useState(null);
    const [showFailedSpiders, setShowFailedSpiders] = useState(false);
    const [showScenarioDialog, setShowScenarioDialog] = useState(false);
    const [scenario, setScenario] = useState("new_spider");
    const [scenarioFields, setScenarioFields] = useState([]);
    const [scenarioLoading, setScenarioLoading] = useState(false);
    const [rerunInPreparationLoading, setRerunInPreparationLoading] = useState(false);
    const [siteDefinitionData, setSiteDefinitionData] = useState(null);
    const [siteDefinitionLoading, setSiteDefinitionLoading] = useState(false);

    const requestTypeOptions = [
        {label: "All", value: null},
        {label: "product_url", value: "product_url"},
        {label: "product_list", value: "product_list"},
        {label: "site_search", value: "site_search"}
    ];
    const createRequestTypeOptions = requestTypeOptions.filter(option => option.value);
    const proxyCountryOptions = ["", ...proxy_countries(t)].map(country => ({
        label: country || "-",
        value: country
    }));
    const probeProxyOptions = requestProbeProxyOptions.map(option => ({
        label: option.label,
        value: option.name
    }));
    const probeBaseSpiderOptions = requestProbeBaseSpiderOptions.map(option => ({
        label: option.name,
        value: option.name
    }));

    const limitOptions = [
        {label: "50", value: 50},
        {label: "100", value: 100},
        {label: "200", value: 200}
    ];

    const archiveStatusOptions = [
        {label: t('request_probe.active_records'), value: "active"},
        {label: t('request_probe.archived_records'), value: "archived"},
        {label: t('actions.all'), value: "all"}
    ];

    const getProxyNameLabel = useCallback((proxyName) => {
        const option = requestProbeProxyOptions.find(item => item.name === proxyName);
        return option ? option.label : (proxyName || "-");
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        const params = {limit: filters.limit || 50, archive_status: filters.archive_status || "active"};
        if (filters.request_type)
            params.request_type = filters.request_type;
        if (filters.website)
            params.website = filters.website.trim();
        // When embedded for a specific campaign (onboarding), only show that
        // campaign's probes; on the standalone admin page show all.
        if (campaignId)
            params.campaign_id = campaignId;
        requestProbeService.listResults(params).then(data => {
            const probes = data || [];
            setItems(probes);
            setSelectedProbe(currentProbe => {
                if (currentProbe && probes.find(probe => probe.id === currentProbe.id))
                    return currentProbe;
                const nextProbe = probes.length > 0 ? probes[0] : null;
                setSelectedGroupHash(nextProbe && nextProbe.selected_response_group ? nextProbe.selected_response_group.response_hash : null);
                setShowFullResponse(false);
                setDetailTabIndex(0);
                setProbeDetailLoaded(false);
                return nextProbe;
            });
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            setError(error);
            setError(null);
        });
    }, [filters, campaignId, requestProbeService, setError]);

    useEffect(() => {
        load();
    }, [load]);

    const selectedProbeId = selectedProbe ? selectedProbe.id : null;
    const selectedProbeWebsite = selectedProbe ? selectedProbe.website : null;
    const selectedProbeRequestType = selectedProbe ? selectedProbe.request_type : null;
    const selectedProbeScrapingRequestId = selectedProbe ? selectedProbe.scraping_request_id : null;
    const selectedProbeScrapingRequest = selectedProbe ? selectedProbe.scraping_request : null;
    const selectedProbeHasResponseGroups = !!(selectedProbe && selectedProbe.response_groups && selectedProbe.response_groups.length > 0);
    const selectedProbeIsProductList = selectedProbeRequestType === "product_list";
    const selectedProbeIsSiteDefined = !!(selectedProbe && selectedProbe.is_site_defined);
    const selectedProbeProductId = selectedProbe ? selectedProbe.product_id : null;
    const selectedProbeHasSiteSearchTab = !!(selectedProbe && selectedProbe.request_type === "site_search" && selectedProbe.site_search_analysis);
    // Single source of truth for the detail tab order — MUST mirror the panel
    // build in productUrlTabsTemplate / productListTabsTemplate. Lazy-loaded tabs
    // derive their index from this so the arithmetic can't drift.
    const detailPanelKeys = useMemo(() => {
        if (!selectedProbe)
            return [];
        let keys;
        if (selectedProbeIsProductList)
            keys = ['product_list_probe', 'html_viewer', 'response_body'];
        else
            keys = ['spiders', 'html_viewer', 'response_body'];
        if (selectedProbeHasSiteSearchTab)
            keys.unshift('site_search_analysis');
        if (selectedProbeIsSiteDefined)
            keys.push('site_definition');
        if (selectedProbeProductId)
            keys.push('product');
        keys.push('scraping_request');
        keys.push('in_preparation');
        keys.push('proxy_attempts');
        return keys;
    }, [selectedProbe, selectedProbeIsProductList, selectedProbeIsSiteDefined, selectedProbeProductId, selectedProbeHasSiteSearchTab]);
    const scrapingRequestTabIndex = detailPanelKeys.indexOf('scraping_request');
    const productTabIndex = detailPanelKeys.indexOf('product');
    const inPrepTabIndex = detailPanelKeys.indexOf('in_preparation');

    // Reset the cached product whenever the selected probe changes.
    useEffect(() => {
        setProbeProduct(null);
    }, [selectedProbeProductId]);

    // Lazy-load the associated product only when the Product tab is opened.
    useEffect(() => {
        if (!selectedProbeProductId || detailTabIndex !== productTabIndex || probeProduct)
            return;

        let isActive = true;
        setProbeProductLoading(true);
        productService.getProduct(selectedProbeProductId).then(product => {
            if (!isActive)
                return;
            setProbeProduct(product || null);
            setProbeProductLoading(false);
        }).catch(() => {
            if (!isActive)
                return;
            setProbeProductLoading(false);
        });
        return () => {
            isActive = false;
        };
    }, [selectedProbeProductId, detailTabIndex, productTabIndex, probeProduct, productService]);

    // Reset cached in-preparation jobs when the probe (website) changes.
    useEffect(() => {
        setInPrepJobs(null);
    }, [selectedProbeWebsite]);

    // Lazy-load in-preparation scraping jobs only when that tab is opened.
    useEffect(() => {
        if (!selectedProbeWebsite || detailTabIndex !== inPrepTabIndex || inPrepJobs)
            return;

        let isActive = true;
        setInPrepJobsLoading(true);
        scraperService.getInPreparationByWebsite(selectedProbeWebsite, 90).then(data => {
            if (!isActive)
                return;
            setInPrepJobs(data || []);
            setInPrepJobsLoading(false);
        }).catch(() => {
            if (!isActive)
                return;
            setInPrepJobs([]);
            setInPrepJobsLoading(false);
        });
        return () => {
            isActive = false;
        };
    }, [selectedProbeWebsite, detailTabIndex, inPrepTabIndex, inPrepJobs, scraperService]);

    useEffect(() => {
        setSiteDefinitionData(null);
        if (!selectedProbeIsSiteDefined || !selectedProbeWebsite)
            return;

        let isActive = true;
        setSiteDefinitionLoading(true);
        scraperService.getSiteDefinitionByUrl(selectedProbeWebsite).then(data => {
            if (!isActive)
                return;
            setSiteDefinitionData(data || null);
            setSiteDefinitionLoading(false);
        }).catch(error => {
            if (!isActive)
                return;
            setSiteDefinitionLoading(false);
            setError(error);
            setError(null);
        });
        return () => {
            isActive = false;
        };
    }, [selectedProbeId, selectedProbeWebsite, selectedProbeIsSiteDefined, scraperService, setError]);

    useEffect(() => {
        if (!selectedProbeId || !selectedProbeWebsite || !selectedProbeRequestType)
            return;
        if (selectedProbeHasResponseGroups)
            return;

        let isActive = true;
        setProbeDetailLoaded(false);
        setDetailLoading(true);
        requestProbeService.getResult(selectedProbeWebsite, selectedProbeRequestType).then(result => {
            if (!isActive)
                return;
            setSelectedProbe(currentProbe => {
                if (!currentProbe || currentProbe.id !== selectedProbeId)
                    return currentProbe;
                return {...currentProbe, ...result};
            });
            setSelectedGroupHash(result && result.selected_response_group ? result.selected_response_group.response_hash : null);
            if (result && result.scraping_request)
                setScrapingRequestStatus(result.scraping_request);
            setShowFullResponse(false);
            setDetailTabIndex(0);
            setShowAllProductUrls(false);
            setShowAllPageUrls(false);
            setProbeDetailLoaded(true);
            setDetailLoading(false);
        }).catch(error => {
            if (!isActive)
                return;
            setDetailLoading(false);
            setError(error);
            setError(null);
        });
        return () => {
            isActive = false;
        };
    }, [selectedProbeId, selectedProbeWebsite, selectedProbeRequestType, selectedProbeHasResponseGroups, requestProbeService, setError]);

    useEffect(() => {
        setScrapingRequestStatus(null);
        if (!selectedProbeId)
            return;
        const shouldLoadScrapingRequest = detailTabIndex === scrapingRequestTabIndex || (!selectedProbeHasResponseGroups && probeDetailLoaded);
        if (!shouldLoadScrapingRequest)
            return;
        if (selectedProbeScrapingRequest) {
            setScrapingRequestStatus(selectedProbeScrapingRequest);
            return;
        }
        if (!selectedProbeScrapingRequestId)
            return;

        let isActive = true;
        setScrapingRequestLoading(true);
        scraperService.getScrapingRequest(selectedProbeScrapingRequestId).then(data => {
            if (!isActive)
                return;
            setScrapingRequestStatus(data);
            setScrapingRequestLoading(false);
        }).catch(error => {
            if (!isActive)
                return;
            setScrapingRequestLoading(false);
            setError(error);
            setError(null);
        });
        return () => {
            isActive = false;
        };
    }, [selectedProbeId, selectedProbeScrapingRequestId, selectedProbeScrapingRequest, selectedProbeHasResponseGroups, probeDetailLoaded, detailTabIndex, scrapingRequestTabIndex, scraperService, setError]);

    const getGroupScore = (group) => {
        const score = getGroupScoreValue(group);
        return score !== "-" ? score : 0;
    };

    const getGroupScoreValue = (group) => {
        const productListProbe = group && (group.product_list_probe || group.selected_product_list_probe || (group.selection_summary && group.selection_summary.product_list_probe));
        if (productListProbe && productListProbe.score !== undefined && productListProbe.score !== null)
            return productListProbe.score;
        if (group && group.best_base_spider_result && group.best_base_spider_result.score !== undefined && group.best_base_spider_result.score !== null)
            return group.best_base_spider_result.score;
        if (group && group.website_spider_result && group.website_spider_result.score !== undefined && group.website_spider_result.score !== null)
            return group.website_spider_result.score;
        return "-";
    };

    const isHttpErrorStatus = (status) => {
        const statusNumber = Number(status);
        return statusNumber >= 400 && statusNumber < 600;
    };

    const getResponseGroups = () => {
        if (!selectedProbe)
            return [];
        const responseGroups = selectedProbe.response_groups || [];
        const groups = responseGroups.length > 0 ? responseGroups : (selectedProbe.selected_response_group ? [selectedProbe.selected_response_group] : []);
        return [...groups].sort((a, b) => {
            const aHasHttpError = isHttpErrorStatus(a.response_status);
            const bHasHttpError = isHttpErrorStatus(b.response_status);
            if (aHasHttpError !== bHasHttpError)
                return aHasHttpError ? 1 : -1;
            return getGroupScore(b) - getGroupScore(a);
        });
    };

    const getSelectedGroup = () => {
        if (!selectedProbe)
            return {};
        const responseGroups = getResponseGroups();
        const hash = selectedGroupHash || (selectedProbe.selected_response_group ? selectedProbe.selected_response_group.response_hash : null);
        return responseGroups.find(group => group.response_hash === hash) || selectedProbe.selected_response_group || responseGroups[0] || {};
    };

    const getProductListProbe = (probe = selectedProbe, group = getSelectedGroup()) => {
        if (group && group.product_list_probe)
            return group.product_list_probe;
        if (probe && probe.selection_summary && probe.selection_summary.product_list_probe)
            return probe.selection_summary.product_list_probe;
        if (probe && probe.selected_product_list_probe)
            return probe.selected_product_list_probe;
        return null;
    };

    const priceSummary = (price) => {
        if (!price)
            return "-";
        const parts = [];
        if (price.value !== undefined && price.value !== null)
            parts.push(price.value);
        if (price.price !== undefined && price.price !== null)
            parts.push(price.price);
        if (price.sale_value !== undefined && price.sale_value !== null)
            parts.push(t('request_probe.sale_value') + ": " + price.sale_value);
        if (price.currency)
            parts.push(price.currency);
        if (price.stock !== undefined && price.stock !== null)
            parts.push(t('request_probe.stock') + ": " + price.stock);
        return parts.length > 0 ? parts.join(" ") : "-";
    };

    const getProductName = (product) => (product && product.name) ? product.name : "-";

    const getProductCode = (product) => {
        if (!product)
            return "";
        return product.sku || product.barcode || product.code || product.product_code || product.mpn || "";
    };

    const scrapedDataExcludedKeys = ["created_at"];

    const collectScrapedData = (result) => {
        const rows = [];
        const append = (object) => {
            if (!object || typeof object !== "object")
                return;
            Object.keys(object).forEach(key => {
                if (scrapedDataExcludedKeys.includes(key))
                    return;
                const value = object[key];
                if (value === null || value === undefined || value === "")
                    return;
                rows.push({key: key, value: typeof value === "object" ? JSON.stringify(value) : String(value)});
            });
        };
        append(result && result.product);
        append(result && result.price);
        return rows;
    };

    const scrapedDataTooltip = (result) => {
        const rows = collectScrapedData(result);
        if (rows.length === 0)
            return t('actions.empty_message');
        return rows.map(row => row.key + ": " + row.value).join("\n");
    };

    const objectRows = (value) => {
        if (!value || typeof value !== "object")
            return [];
        return Object.keys(value).map(key => ({key: key, value: value[key]}));
    };

    const valueTemplate = (value) => {
        if (value === null || value === undefined || value === "")
            return "-";
        if (typeof value === "boolean")
            return value ? t('yes_no.yes') : t('yes_no.no');
        if (Array.isArray(value))
            return value.length > 0 ? value.join(", ") : "-";
        if (typeof value === "object")
            return JSON.stringify(value);
        const stringValue = String(value);
        if (/^https?:\/\//.test(stringValue))
            return <a href={stringValue} target="_blank" rel="noopener noreferrer">{stringValue}</a>;
        return stringValue;
    };

    const getZyteJobUrl = (jobKey) => {
        if (!jobKey)
            return null;
        const parts = String(jobKey).split("/");
        if (parts.length !== 3)
            return null;
        return `https://app.zyte.com/p/${parts[0]}/job/${parts[1]}/${parts[2]}`;
    };

    const jobKeysTemplate = (value) => {
        const jobKeys = Array.isArray(value) ? value : String(value || "").split(",").map(item => item.trim()).filter(Boolean);
        if (jobKeys.length === 0)
            return "-";
        return <div>
            {jobKeys.map((jobKey, index) => {
                const zyteUrl = getZyteJobUrl(jobKey);
                return <div key={jobKey + index}>
                    {zyteUrl ? <a href={zyteUrl} target="_blank" rel="noopener noreferrer">{jobKey}</a> : jobKey}
                </div>;
            })}
        </div>;
    };

    const scrapingRequestValueTemplate = (row) => {
        if (row.key === "job_key" || row.key === "job_keys")
            return jobKeysTemplate(row.value);
        return valueTemplate(row.value);
    };

    const listValue = (value) => {
        if (!value)
            return [];
        if (Array.isArray(value))
            return value.filter(Boolean);
        return String(value).split(",").map(item => item.trim()).filter(Boolean);
    };

    const selectProbe = (probe) => {
        setSelectedProbe(probe);
        setSelectedGroupHash(probe && probe.selected_response_group ? probe.selected_response_group.response_hash : null);
        setShowFullResponse(false);
        setDetailTabIndex(0);
        setShowAllProductUrls(false);
        setShowAllPageUrls(false);
        setProbeDetailLoaded(false);
    };

    // Refresh the currently selected probe's result (e.g. after its Zyte job finishes).
    const refreshSelectedProbe = () => {
        if (!selectedProbe || !selectedProbeWebsite || !selectedProbeRequestType)
            return;
        setDetailLoading(true);
        requestProbeService.getResult(selectedProbeWebsite, selectedProbeRequestType).then(result => {
            setSelectedProbe(currentProbe => {
                if (!currentProbe || currentProbe.id !== selectedProbeId)
                    return currentProbe;
                return {...currentProbe, ...result};
            });
            setSelectedGroupHash(result && result.selected_response_group ? result.selected_response_group.response_hash : null);
            if (result && result.scraping_request)
                setScrapingRequestStatus(result.scraping_request);
            setProbeDetailLoaded(true);
            setDetailLoading(false);
        }).catch(error => {
            setDetailLoading(false);
            setError(error);
            setError(null);
        });
    };

    const createProbe = () => {
        const url = newProbeUrl.trim();
        if (!url)
            return;

        setNewProbeLoading(true);
        const isSiteSearch = newProbeRequestType === 'site_search';
        requestProbeService.createProbe(url, newProbeRequestType, newProbeProxyCountry, {
            selected_proxies: newProbeSelectedProxies,
            selected_base_spiders: newProbeSelectedBaseSpiders,
            campaign_id: newProbeCampaignId || campaignId || undefined,
            product_id: newProbeProductId || undefined,
            search_url_template: isSiteSearch ? (newProbeSearchUrlTemplate.trim() || undefined) : undefined,
            search_keyword: isSiteSearch ? (newProbeSearchKeyword.trim() || undefined) : undefined
        }).then(() => {
            setNewProbeLoading(false);
            toast.current.show({severity: 'success', summary: t('request_probe.probe'), detail: t('message_detail.successful')});
            setNewProbeUrl("");
            setNewProbeSelectedProxies([]);
            setNewProbeSelectedBaseSpiders([]);
            setNewProbeSearchUrlTemplate("");
            setNewProbeSearchKeyword("");
            setNewProbeProductId(null);
            setNewProbeCampaignId(null);
            setNewProbeIsRerun(false);
            setShowCreateDialog(false);
            load();
        }).catch(error => {
            setNewProbeLoading(false);
            setError(error);
            setError(null);
        });
    };

    const rerunProbe = () => {
        if (!selectedProbe || !selectedProbe.url || !selectedProbe.request_type)
            return;

        // Open the create dialog pre-filled with the selected probe's parameters
        // so the user can review/edit them before re-running.
        setNewProbeUrl(selectedProbe.url);
        setNewProbeRequestType(selectedProbe.request_type);
        setNewProbeProxyCountry(selectedProbe.proxy_country || "");
        setNewProbeSelectedProxies(listValue(selectedProbe.selected_proxies));
        setNewProbeSelectedBaseSpiders(listValue(selectedProbe.selected_base_spiders));
        setNewProbeSearchUrlTemplate(selectedProbe.search_url_template || "");
        setNewProbeSearchKeyword(selectedProbe.search_keyword || "");
        setNewProbeProductId(selectedProbe.product_id || null);
        setNewProbeCampaignId(selectedProbe.campaign_id || null);
        setNewProbeIsRerun(true);
        setShowCreateDialog(true);
    };

    const proxyNameToProxyType = {
        is_proxy_used: "SmartProxyManager",
        is_inhouse_unblocker_used: "InHouseUnblocker",
        is_webshare_used: "Webshare",
        is_proxy_zyte_api_used: "ZyteAPI",
        is_scraperapi_used: "ScraperAPI"
    };

    const getGroupProxyName = (group) => {
        if (!group)
            return null;
        if (group.current_proxy && group.current_proxy.name)
            return group.current_proxy.name;
        const proxies = group.proxies || [];
        const realProxy = proxies.find(name => name && name !== "no_proxy");
        return realProxy || (proxies.length > 0 ? proxies[0] : null);
    };

    const openSiteDefinition = (result) => {
        if (!selectedProbe)
            return;
        const website = selectedProbe.website || getHostName(selectedProbe.url || "");
        const seed = {
            url: website,
            spider_name: result && result.spider_name ? result.spider_name : "",
            country: "Universal",
            sector: "Generic"
        };
        const proxyName = getGroupProxyName(getSelectedGroup());
        if (proxyName && proxyName !== "no_proxy") {
            seed.is_proxy_used = true;
            if (proxyNameToProxyType[proxyName])
                seed.proxy_type = proxyNameToProxyType[proxyName];
            if (selectedProbe.proxy_country)
                seed.proxy_country = selectedProbe.proxy_country;
        }
        setSiteDefinitionSeed(seed);
        setSiteDefinitionProbeId(selectedProbe.id || null);
        setShowSiteDefinitionDialog(true);
    };

    const rerunInPreparationJobs = () => {
        if (!selectedProbe || !selectedProbe.website)
            return;
        setRerunInPreparationLoading(true);
        scraperService.rerunInPreparationByWebsite(selectedProbe.website, 90).then((data) => {
            setRerunInPreparationLoading(false);
            const rerunCount = data && data.rerun_count ? data.rerun_count : 0;
            toast.current.show({
                severity: rerunCount > 0 ? 'success' : 'info',
                summary: t('request_probe.rerun_scraping_jobs'),
                detail: rerunCount > 0 ? t('request_probe.rerun_scraping_jobs_done', {count: rerunCount}) : t('request_probe.rerun_scraping_jobs_none')
            });
        }).catch(error => {
            setRerunInPreparationLoading(false);
            setError(error);
            setError(null);
        });
    };

    const archiveProbeAfterDefine = (probeId) => {
        if (!probeId)
            return;
        requestProbeService.archiveProbe(probeId, true).then(() => {
            toast.current.show({severity: 'success', summary: t('request_probe.archived'), detail: t('message_detail.successful')});
            setSelectedProbe(currentProbe => (currentProbe && currentProbe.id === probeId) ? null : currentProbe);
            load();
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    // Open the existing site definition for editing (e.g. to enable site search).
    const openUpdateSiteDefinition = () => {
        if (!siteDefinitionData)
            return;
        setSiteDefinitionSeed(siteDefinitionData);   // has id -> SiteDefinitionAdd edit mode (PUT)
        setSiteDefinitionProbeId(selectedProbe ? selectedProbe.id : null);
        setShowSiteDefinitionDialog(true);
    };

    const onSiteDefinitionComplete = (isCancel) => {
        const probeId = siteDefinitionProbeId;
        const wasEdit = !!(siteDefinitionSeed && siteDefinitionSeed.id);
        setShowSiteDefinitionDialog(false);
        setSiteDefinitionProbeId(null);
        if (isCancel)
            return;
        // After defining/updating the site, also rerun in-preparation scraping jobs
        // for it (same as the "Rerun Scraping Jobs" action). Triggered before
        // archive so selectedProbe.website is still available.
        rerunInPreparationJobs();
        if (wasEdit) {
            // reload the definition so updated flags (e.g. is_site_searching_ready) show
            if (selectedProbeWebsite)
                scraperService.getSiteDefinitionByUrl(selectedProbeWebsite)
                    .then(data => setSiteDefinitionData(data || null)).catch(() => {});
        } else {
            archiveProbeAfterDefine(probeId);
        }
    };

    const archiveSelectedProbe = () => {
        if (!selectedProbe || !selectedProbe.id)
            return;
        const probeId = selectedProbe.id;
        const nextArchived = !selectedProbe.is_archived;
        setArchiveLoading(true);
        requestProbeService.archiveProbe(probeId, nextArchived).then(() => {
            setArchiveLoading(false);
            toast.current.show({severity: 'success', summary: nextArchived ? t('request_probe.archived') : t('request_probe.unarchived'), detail: t('message_detail.successful')});
            setSelectedProbe(currentProbe => (currentProbe && currentProbe.id === probeId) ? {...currentProbe, is_archived: nextArchived} : currentProbe);
            load();
        }).catch(error => {
            setArchiveLoading(false);
            setError(error);
            setError(null);
        });
    };

    const deleteSelectedProbe = () => {
        if (!selectedProbe || !selectedProbe.id) {
            setShowDeleteDialog(false);
            return;
        }
        const probeId = selectedProbe.id;
        setDeleteLoading(true);
        requestProbeService.deleteProbe(probeId).then(() => {
            setDeleteLoading(false);
            setShowDeleteDialog(false);
            toast.current.show({severity: 'success', summary: t('actions.delete'), detail: t('message_detail.successful')});
            setSelectedProbe(currentProbe => (currentProbe && currentProbe.id === probeId) ? null : currentProbe);
            load();
        }).catch(error => {
            setDeleteLoading(false);
            setShowDeleteDialog(false);
            setError(error);
            setError(null);
        });
    };

    const isSiteSearchProbe = () => selectedProbe && selectedProbe.request_type === "site_search";

    const buildPrompt = () => {
        if (!selectedProbe)
            return;
        // Both product and site_search prompts are scenario-driven: open the
        // scenario picker, pre-filling the most likely scenario + fields.
        if (isSiteSearchProbe()) {
            const analysis = selectedProbe.site_search_analysis;
            setScenario("new");
            setScenarioFields(analysis && analysis.best_field ? [analysis.best_field] : []);
        } else {
            // If the probe captured a parse exception, default to the exception scenario.
            setScenario(getProbeExceptionDetail() ? "exception" : "new_spider");
            setScenarioFields(detectMissingProductFields());
        }
        setShowScenarioDialog(true);
    };

    // Field keys mirror PRODUCT_FIELDS in the backend product_url prompt builder.
    const PRODUCT_FIELD_KEYS = ['name', 'brand', 'price', 'sale_price', 'currency', 'stock', 'sku', 'barcode', 'mpn', 'upc', 'ean', 'image', 'variants'];
    // Search field keys mirror SEARCH_FIELDS in the site-search prompt builder.
    const SEARCH_FIELD_KEYS = ['name', 'sku', 'barcode', 'mpn', 'upc', 'ean'];

    const fieldLabel = (k) => ({
        name: t('table_headers.name'), brand: t('product.brand'), price: t('product.price'),
        sale_price: t('request_probe.field_sale_price'), currency: t('product.currency'),
        stock: t('product.stock'), sku: 'SKU', barcode: 'Barcode', mpn: 'MPN', upc: 'UPC',
        ean: 'EAN', image: t('product.image'), variants: t('request_probe.field_variants')
    }[k] || k);

    const scenarioFieldKeys = () => (isSiteSearchProbe() ? SEARCH_FIELD_KEYS : PRODUCT_FIELD_KEYS);
    const scenarioFieldOptions = () => scenarioFieldKeys().map(k => ({label: fieldLabel(k), value: k}));

    const productScenarioOptions = [
        {label: 'scenario_new_spider', value: 'new_spider'},
        {label: 'scenario_exception', value: 'exception'},
        {label: 'scenario_missing_fields', value: 'missing_fields'},
        {label: 'scenario_wrong_data', value: 'wrong_data'},
        {label: 'scenario_site_changed', value: 'site_changed'},
        {label: 'scenario_js_headless', value: 'js_headless'},
        {label: 'scenario_variants', value: 'variants'},
        {label: 'scenario_blocked', value: 'blocked'},
        {label: 'scenario_structured_base', value: 'structured_base'},
    ];
    const siteSearchScenarioOptions = [
        {label: 'ss_scenario_new', value: 'new'},
        {label: 'ss_scenario_wrong_url', value: 'wrong_url'},
        {label: 'ss_scenario_no_results', value: 'no_results'},
        {label: 'ss_scenario_wrong_field', value: 'wrong_field'},
        {label: 'ss_scenario_irrelevant', value: 'irrelevant'},
        {label: 'ss_scenario_js_api', value: 'js_api'},
        {label: 'ss_scenario_blocked', value: 'blocked'},
    ];
    const scenarioOptions = () => (isSiteSearchProbe() ? siteSearchScenarioOptions : productScenarioOptions)
        .map(o => ({label: t('request_probe.' + o.label), value: o.value}));

    // Which scenarios show the focused-fields multiselect.
    const scenarioUsesFields = (s) => isSiteSearchProbe()
        ? s === 'wrong_field'
        : (s === 'missing_fields' || s === 'wrong_data');

    // Best-effort: which product fields did the probe's best spider result NOT
    // extract? Pre-selects them for the "missing data" scenario.
    const detectMissingProductFields = () => {
        const group = getSelectedGroup();
        const list = (group && group.base_spider_results) || [];
        let best = group && group.website_spider_result;
        if (list.length > 0) {
            const top = list.reduce((a, b) => ((b.score || 0) > (a.score || 0) ? b : a));
            if (!best || (top.score || 0) > (best.score || 0))
                best = top;
        }
        const extracted = {...((best && best.product) || {}), ...((best && best.price) || {})};
        const present = (k) => {
            const v = extracted[k] !== undefined ? extracted[k] : (k === 'price' ? extracted.value : undefined);
            return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
        };
        return PRODUCT_FIELD_KEYS.filter(k => !present(k));
    };

    // Exception captured by the probe's spider results (structured exception_detail
    // preferred, else the plain error string). Used for the "exception" scenario.
    const getProbeExceptionDetail = () => {
        const group = getSelectedGroup();
        const candidates = [group && group.website_spider_result, ...((group && group.base_spider_results) || [])];
        for (const result of candidates) {
            if (result && result.exception_detail && typeof result.exception_detail === "object")
                return result.exception_detail;
        }
        for (const result of candidates) {
            if (result && result.error)
                return result.error;
        }
        return null;
    };

    const generatePrompt = () => {
        if (!selectedProbe)
            return;
        const group = getSelectedGroup();
        const responseHash = (group && group.response_hash) ? group.response_hash :
            (selectedProbe.selected_response_group ? selectedProbe.selected_response_group.response_hash : null);
        const downloadUrl = selectedProbe.id ? requestProbeService.getResponseDownloadUrl(selectedProbe.id, responseHash) : "";
        const useFields = scenarioUsesFields(scenario) ? scenarioFields : [];

        const onPrompt = (result) => {
            setScenarioLoading(false);
            setPromptText((result && result.prompt) || "");
            setShowScenarioDialog(false);
            setShowPromptDialog(true);
        };
        const onFail = (error) => {
            setScenarioLoading(false);
            setError(error);
            setError(null);
        };

        setScenarioLoading(true);
        if (isSiteSearchProbe()) {
            const siteSearchProbe = (group && group.site_search_probe) ||
                (selectedProbe.selected_response_group && selectedProbe.selected_response_group.site_search_probe) || {};
            const analysis = selectedProbe.site_search_analysis;
            const bestField = analysis && analysis.fields && analysis.fields.find(f => f.field === analysis.best_field);
            const detectedSpider = (group && group.website_spider_result && group.website_spider_result.spider_name)
                || selectedProbe.selected_spider
                || (group && group.best_spider_result && group.best_spider_result.spider_name);
            aiJobService.buildSiteSearchPrompt(selectedProbe.website || "", {
                spider_name: detectedSpider,
                search_url: (bestField && bestField.search_url) || (analysis && analysis.detected_template) || selectedProbe.url || siteSearchProbe.search_url,
                keyword: (bestField && bestField.query) || siteSearchProbe.keyword,
                sample_result_urls: (bestField && bestField.sample_links) || siteSearchProbe.sample_result_urls,
                response_download_url: downloadUrl,
                scenario: scenario,
                fields: useFields,
            }).then(onPrompt).catch(onFail);
        } else {
            aiJobService.buildProductUrlPrompt(selectedProbe.website || "", {
                product_url: selectedProbe.url || "",
                scenario: scenario,
                fields: useFields,
                response_download_url: downloadUrl,
                error_detail: scenario === 'exception' ? getProbeExceptionDetail() : undefined,
            }).then(onPrompt).catch(onFail);
        }
    };

    const copyPrompt = () => {
        navigator.clipboard.writeText(promptText || "").then(() => {
            toast.current.show({severity: 'success', summary: t('actions.copy'), detail: t('actions.copied')});
        });
    };

    const copyResponseBody = (responseText) => {
        navigator.clipboard.writeText(responseText || "").then(() => {
            toast.current.show({severity: 'success', summary: t('actions.copy'), detail: t('actions.copied')});
        });
    };

    const responseGroupsTemplate = () => {
        const responseGroups = getResponseGroups();
        const selectedGroup = getSelectedGroup();
        const groupOptions = responseGroups.map(group => ({
            label: [
                t('request_probe.response_status') + ": " + (group.response_status || "-"),
                t('request_probe.proxies') + ": " + (group.proxies && group.proxies.length > 0 ? group.proxies.map(getProxyNameLabel).join(", ") : "-"),
                t('request_probe.score') + ": " + getGroupScoreValue(group)
            ].join(" | "),
            value: group.response_hash || "",
            group: group
        }));
        const optionTemplate = (option) => {
            if (!option)
                return null;
            const group = option.group || {};
            return <div style={{display: "flex", gap: "1em", flexWrap: "wrap"}}>
                <span><b>{t('request_probe.response_status')}:</b> {group.response_status || "-"}</span>
                <span><b>{t('request_probe.proxies')}:</b> {group.proxies && group.proxies.length > 0 ? group.proxies.map(getProxyNameLabel).join(", ") : "-"}</span>
                <span><b>{t('request_probe.score')}:</b> {getGroupScoreValue(group)}</span>
            </div>;
        };

        return <div style={{marginTop: "1em", marginBottom: "1em"}}>
            <b>{t('request_probe.response_groups')}</b>
            {responseGroups.length === 0 &&
                <div style={{padding: "1em", color: "#6b7280", border: "1px solid #e5e7eb", marginTop: ".5em"}}>
                    {t('actions.empty_message')}
                </div>}
            {responseGroups.length > 0 &&
                <Dropdown value={selectedGroup.response_hash || ""}
                          options={groupOptions}
                          optionLabel="label"
                          optionValue="value"
                          itemTemplate={optionTemplate}
                          valueTemplate={optionTemplate}
                          onChange={(e) => {
                              setSelectedGroupHash(e.value);
                              setShowFullResponse(false);
                              setDetailTabIndex(0);
                          }}
                          style={{width: "100%", marginTop: ".5em"}}/>}
        </div>;
    };

    // Unified spider results: the website (site-specific) spider + all base
    // spiders in one table; a row's detail (scraped data, price, price_check,
    // exception) opens in a dialog.
    const getSpiderRows = () => {
        const group = getSelectedGroup();
        const rows = [];
        if (group && group.website_spider_result)
            rows.push({...group.website_spider_result, _is_website: true});
        (group && group.base_spider_results ? group.base_spider_results : []).forEach(r => rows.push(r));
        return rows;
    };

    const spidersTemplate = () => {
        const rows = getSpiderRows();
        if (rows.length === 0)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;
        // Failed BASE spiders are hidden by default (the website spider is always
        // shown). A toggle reveals them.
        const failedBaseCount = rows.filter(r => !r._is_website && !r.success).length;
        const filtered = showFailedSpiders ? rows : rows.filter(r => r._is_website || r.success);
        // Website spider first, then successful, then by score.
        const sorted = [...filtered].sort((a, b) => {
            if (!!a._is_website !== !!b._is_website)
                return a._is_website ? -1 : 1;
            if (!!a.success !== !!b.success)
                return a.success ? -1 : 1;
            return (b.score || 0) - (a.score || 0);
        });
        return <div>
            {failedBaseCount > 0 &&
                <div style={{display: "flex", justifyContent: "flex-end"}}>
                    <Button className="p-button-text p-button-sm"
                            icon={showFailedSpiders ? "pi pi-eye-slash" : "pi pi-eye"}
                            label={showFailedSpiders ? t('request_probe.hide_others') : t('request_probe.show_others') + " (" + failedBaseCount + ")"}
                            onClick={() => setShowFailedSpiders(!showFailedSpiders)}/>
                </div>}
            <Tooltip target=".scraped-data-cell" position="left" showDelay={0} hideDelay={0} style={{maxWidth: "32em"}}/>
            <DataTable value={sorted} responsive={true} style={{marginTop: ".5em"}}>
                <Column header={t('request_probe.base_spider')} sortable field="spider_name" style={{width: "15em"}}
                        body={(result) => <span style={{display: "inline-flex", alignItems: "center", gap: ".4em"}}>
                            {result.spider_name || "-"}
                            {result._is_website && <span className="p-tag p-tag-info">{t('request_probe.website_spider')}</span>}
                        </span>}/>
                <Column field="success" header={t('request_probe.success')} sortable body={(result) =>
                    <span className={result.success ? "p-tag p-tag-success" : "p-tag p-tag-danger"}>{result.success ? t('actions.active') : t('actions.passive')}</span>}
                        style={{width: "8em"}}/>
                <Column field="score" header={t('request_probe.score')} sortable style={{width: "7em"}}/>
                <Column header={t('request_probe.data')} body={(result) => {
                    const product = result.product || {};
                    const code = getProductCode(product);
                    return <span className="scraped-data-cell"
                                 data-pr-tooltip={scrapedDataTooltip(result)}
                                 data-pr-position="left"
                                 style={{cursor: "help", whiteSpace: "normal", display: "inline-block"}}>
                        <div><b>{getProductName(product)}</b></div>
                        {code ? <div style={{color: "#6b7280"}}>{code}</div> : null}
                    </span>;
                }} />
                <Column header={t('request_probe.price_check')} style={{width: "8em"}} body={(result) => result.price_check
                    ? <span className={result.price_check.success ? "p-tag p-tag-success" : "p-tag p-tag-danger"}>{result.price_check.success ? t('actions.active') : t('actions.passive')}</span>
                    : "-"}/>
                <Column header={t('request_probe.error')} style={{width: "6em"}} body={(result) =>
                    (result.error || result.exception_detail || (result.price_check && (result.price_check.error || result.price_check.exception_detail)))
                        ? <i className="pi pi-exclamation-triangle" style={{color: "#ef4444"}}/> : "-"}/>
                <Column header={t('table_headers.action')} style={{width: "16em"}} body={(result) =>
                    <span style={{display: "flex", gap: ".4em"}}>
                        <Button label={t('actions.detail')} icon="pi pi-search" className="p-button-outlined p-button-sm"
                                onClick={() => setSpiderDetail(result)}/>
                        {result.success && !result._is_website &&
                            <Button icon="pi pi-plus" className="p-button-outlined p-button-success p-button-sm"
                                    tooltip={t('request_probe.define_website')} tooltipOptions={{position: 'bottom'}}
                                    onClick={() => openSiteDefinition(result)}/>}
                    </span>}/>
            </DataTable>
        </div>;
    };

    const spiderResultTemplate = (result) => {
        if (!result)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;
        return <div className="p-grid">
            <div className="p-col-12 p-md-3">
                <b>{t('request_probe.base_spider')}</b>
                <div>{result.spider_name || "-"}</div>
            </div>
            <div className="p-col-12 p-md-3">
                <b>{t('request_probe.success')}</b>
                <div><span className={result.success ? "p-tag p-tag-success" : "p-tag p-tag-danger"}>{result.success ? t('actions.active') : t('actions.passive')}</span></div>
            </div>
            <div className="p-col-12 p-md-3">
                <b>{t('request_probe.score')}</b>
                <div>{result.score !== undefined && result.score !== null ? result.score : "-"}</div>
            </div>
            <div className="p-col-12 p-md-3">
                <b>{t('request_probe.price')}</b>
                <div>{priceSummary(result.price)}</div>
            </div>
            <div className="p-col-12 p-md-6">
                <b>{t('request_probe.product')}</b>
                <DataTable value={objectRows(result.product)} responsive={true} style={{marginTop: ".5em"}}>
                    <Column field="key" header={t('table_headers.name')} style={{width: "14em"}}/>
                    <Column header={t('table_headers.value')} body={(row) => valueTemplate(row.value)}/>
                </DataTable>
            </div>
            <div className="p-col-12 p-md-6">
                <b>{t('request_probe.price')}</b>
                <DataTable value={objectRows(result.price)} responsive={true} style={{marginTop: ".5em"}}>
                    <Column field="key" header={t('table_headers.name')} style={{width: "14em"}}/>
                    <Column header={t('table_headers.value')} body={(row) => valueTemplate(row.value)}/>
                </DataTable>
            </div>
            {(result.exception_detail || result.error) &&
                <div className="p-col-12">
                    {result.exception_detail && typeof result.exception_detail === "object"
                        ? <div style={{whiteSpace: "normal", wordBreak: "break-word", border: "1px solid #fca5a5", background: "#fef2f2", borderRadius: 6, padding: ".6em .8em"}}>
                            {Object.entries(result.exception_detail).map(([key, value]) =>
                                (value !== null && value !== undefined && value !== "")
                                    ? <div key={key} style={{marginBottom: ".35em"}}>
                                        <b>{key}: </b>{typeof value === "object" ? JSON.stringify(value) : String(value)}
                                    </div>
                                    : null)}
                        </div>
                        : <span className="p-tag p-tag-danger" style={{whiteSpace: "normal"}}>{result.error}</span>}
                </div>}
            {result.price_check &&
                <div className="p-col-12" style={{marginTop: ".5em", borderTop: "1px solid #eee", paddingTop: ".75em"}}>
                    <b>{t('request_probe.price_check')}</b>
                    <div className="p-grid" style={{marginTop: ".25em"}}>
                        <div className="p-col-12 p-md-3">
                            <div style={{color: "#6b7280", fontSize: ".85em"}}>{t('request_probe.success')}</div>
                            <span className={result.price_check.success ? "p-tag p-tag-success" : "p-tag p-tag-danger"}>{result.price_check.success ? t('actions.active') : t('actions.passive')}</span>
                        </div>
                        <div className="p-col-12 p-md-3">
                            <div style={{color: "#6b7280", fontSize: ".85em"}}>{t('request_probe.price')}</div>
                            <div>{priceSummary(result.price_check.price)}</div>
                        </div>
                        {result.price_check.name &&
                            <div className="p-col-12 p-md-6">
                                <div style={{color: "#6b7280", fontSize: ".85em"}}>{t('request_probe.competitor_name')}</div>
                                <div>{result.price_check.name}</div>
                            </div>}
                        {result.price_check.price &&
                            <div className="p-col-12 p-md-6">
                                <DataTable value={objectRows(result.price_check.price)} responsive={true} style={{marginTop: ".25em"}}>
                                    <Column field="key" header={t('table_headers.name')} style={{width: "14em"}}/>
                                    <Column header={t('table_headers.value')} body={(row) => valueTemplate(row.value)}/>
                                </DataTable>
                            </div>}
                        {(result.price_check.exception_detail || result.price_check.error) &&
                            <div className="p-col-12">
                                {result.price_check.exception_detail && typeof result.price_check.exception_detail === "object"
                                    ? <div style={{whiteSpace: "normal", wordBreak: "break-word", border: "1px solid #fca5a5", background: "#fef2f2", borderRadius: 6, padding: ".6em .8em"}}>
                                        {Object.entries(result.price_check.exception_detail).map(([key, value]) =>
                                            (value !== null && value !== undefined && value !== "")
                                                ? <div key={key} style={{marginBottom: ".35em"}}><b>{key}: </b>{typeof value === "object" ? JSON.stringify(value) : String(value)}</div>
                                                : null)}
                                    </div>
                                    : <span className="p-tag p-tag-danger" style={{whiteSpace: "normal"}}>{result.price_check.error}</span>}
                            </div>}
                    </div>
                </div>}
        </div>;
    };

    const spiderDetailDialog = () => {
        if (!spiderDetail)
            return null;
        const selectedGroup = getSelectedGroup();
        const currentProxy = spiderDetail._is_website
            ? (selectedGroup.current_proxy || spiderDetail.current_proxy)
            : null;
        return <Dialog header={(spiderDetail.spider_name || t('request_probe.base_spider'))}
                       visible={true} style={{maxWidth: "60em", width: "92vw"}} modal={true}
                       onHide={() => setSpiderDetail(null)}>
            {spiderDetail._is_website &&
                <div className="p-grid" style={{marginBottom: ".5em"}}>
                    <div className="p-col-12 p-md-4">
                        <b>{t('request_probe.current_proxy')}</b>
                        <div>{getProxyNameLabel(currentProxy ? currentProxy.name : null)}</div>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <b>{t('request_probe.proxy_source')}</b>
                        <div>{currentProxy && currentProxy.source ? currentProxy.source : "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <b>{t('request_probe.proxy_type')}</b>
                        <div>{currentProxy && currentProxy.proxy_type ? currentProxy.proxy_type : "-"}</div>
                    </div>
                </div>}
            {spiderResultTemplate(spiderDetail)}
        </Dialog>;
    };

    const responseBodyTemplate = () => {
        const selectedGroup = getSelectedGroup();
        const responseText = selectedGroup.response_text || "";
        if (!responseText)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;
        const responsePreview = showFullResponse ? responseText : responseText.substring(0, 3000);
        return <React.Fragment>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <b>{t('request_probe.response_body_preview')}</b>
                <div>
                    <Button label={t('actions.copy')}
                            icon="pi pi-copy"
                            className="p-button-outlined p-button-info"
                            style={{marginRight: ".5em"}}
                            onClick={() => copyResponseBody(responseText)}/>
                    {responseText.length > 3000 &&
                        <Button label={showFullResponse ? t('request_probe.hide_full_response') : t('request_probe.show_full_response')}
                                className="p-button-text"
                                onClick={() => setShowFullResponse(!showFullResponse)}/>}
                </div>
            </div>
            <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "22em", overflow: "auto", fontFamily: "monospace", border: "1px solid #ddd", padding: "1em", marginTop: ".5em"}}>{responsePreview}</pre>
        </React.Fragment>;
    };

    const htmlViewerTemplate = () => {
        const selectedGroup = getSelectedGroup();
        const responseText = selectedGroup.response_text || "";
        if (!responseText)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;
        return <iframe title={t('request_probe.html_viewer')} sandbox="" srcDoc={responseText}
                       style={{width: "100%", minHeight: "32em", border: "1px solid #ddd", background: "#fff"}}/>;
    };

    const proxyAttemptsTemplate = () => {
        const selectedGroup = getSelectedGroup();
        const requests = selectedGroup.requests || [];
        if (requests.length === 0)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;
        return <div>
            <DataTable value={requests} responsive={true} style={{marginTop: ".5em"}}>
                <Column field="proxy_name" header={t('request_probe.proxy_name')} body={(request) => getProxyNameLabel(request.proxy_name)} style={{width: "12em"}}/>
                <Column field="response_url" header={t('request_probe.response_url')} body={(request) => request.response_url ? <a href={request.response_url} target="_blank" rel="noopener noreferrer">{request.response_url}</a> : "-"} />
                <Column field="error" header={t('request_probe.error')} body={(request) => request.error || "-"} />
                <Column field="created_at" header={t('request_probe.created_at')} body={(request) => request.created_at ? toDateTimeString(request.created_at) : "-"} style={{width: "12em"}}/>
            </DataTable>
        </div>;
    };

    const fieldTemplate = (label, value, className = "p-col-12 p-md-3") => {
        return <div className={className}>
            <b>{label}</b>
            <div style={{wordBreak: "break-word"}}>{valueTemplate(value)}</div>
        </div>;
    };

    const urlListTemplate = (title, urls, showAll, setShowAll) => {
        const list = urls || [];
        if (list.length === 0)
            return null;
        const visibleUrls = showAll ? list : list.slice(0, 20);
        return <div style={{marginTop: "1em"}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <b>{title}</b>
                {list.length > 20 &&
                    <Button label={showAll ? t('request_probe.show_less') : t('request_probe.show_more')}
                            className="p-button-text"
                            onClick={() => setShowAll(!showAll)}/>}
            </div>
            <div style={{border: "1px solid #e5e7eb", padding: ".75em", maxHeight: "18em", overflow: "auto", marginTop: ".5em"}}>
                {visibleUrls.map((url, index) => <div key={url + index} style={{marginBottom: ".35em", wordBreak: "break-word"}}>
                    <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                </div>)}
            </div>
        </div>;
    };

    const productListProbeTemplate = () => {
        const selectedGroup = getSelectedGroup();
        const productListProbe = getProductListProbe(selectedProbe, selectedGroup);
        if (!productListProbe)
            return <div style={{padding: "1em", color: "#6b7280"}}>
                {selectedProbe && selectedProbe.state === "failed" ? t('request_probe.failed') : t('request_probe.product_list_unavailable')}
            </div>;
        const sampleProductResult = productListProbe.sample_product_result || {};
        const sampleSpiderResult = sampleProductResult.best_spider_result || sampleProductResult.best_base_spider_result;
        const hasError = productListProbe.success === false || !!productListProbe.sample_product_error;
        return <div>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1em", flexWrap: "wrap"}}>
                <b>{t('request_probe.product_list_probe')}</b>
                <span className={hasError ? "p-tag p-tag-danger" : "p-tag p-tag-success"}>
                    {hasError ? t('request_probe.failed') : t('request_probe.successful')}
                </span>
            </div>
            <div className="p-grid" style={{marginTop: ".5em"}}>
                {fieldTemplate(t('request_probe.entry_callback'), productListProbe.entry_callback)}
                {fieldTemplate(t('request_probe.detected_pages'), productListProbe.page_count)}
                {fieldTemplate(t('request_probe.first_page_products'), productListProbe.first_page_product_count)}
                {fieldTemplate(t('request_probe.estimated_products'), productListProbe.estimated_product_count)}
                {fieldTemplate(t('request_probe.sample_product_response_status'), productListProbe.sample_product_response_status)}
                {fieldTemplate(t('request_probe.sample_product_url'), productListProbe.sample_product_url, "p-col-12 p-md-6")}
                {productListProbe.sample_product_error && fieldTemplate(t('request_probe.sample_product_error'), productListProbe.sample_product_error, "p-col-12")}
            </div>
            {sampleSpiderResult &&
                <div style={{marginTop: "1em"}}>
                    <b>{t('request_probe.sample_product_parse')}</b>
                    <div style={{marginTop: ".5em"}}>{spiderResultTemplate(sampleSpiderResult)}</div>
                </div>}
            {urlListTemplate(t('request_probe.product_urls'), productListProbe.product_urls, showAllProductUrls, setShowAllProductUrls)}
            {urlListTemplate(t('request_probe.page_urls'), productListProbe.page_urls, showAllPageUrls, setShowAllPageUrls)}
        </div>;
    };

    const scrapingRequestStatusTemplate = () => {
        return <div style={{padding: "1em", border: "1px solid #e5e7eb", color: "#374151"}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1em", flexWrap: "wrap"}}>
                <b>{t('menu_item.scraping_request')}</b>
                {scrapingRequestStatus && scrapingRequestStatus.state &&
                    <span className="p-tag p-tag-info">{t('scraping_request.statuses.' + scrapingRequestStatus.state)}</span>}
            </div>
            {scrapingRequestLoading &&
                <div style={{marginTop: ".75em"}}>
                    <Loading/>
                </div>}
            {!scrapingRequestLoading &&
                <div className="p-grid" style={{marginTop: ".5em"}}>
                    <div className="p-col-12 p-md-4">
                        <b>{t('scraping_request.id')}</b>
                        <div style={{wordBreak: "break-word"}}>{selectedProbeScrapingRequestId || "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <b>{t('scraping_request.state')}</b>
                        <div>{scrapingRequestStatus && scrapingRequestStatus.state ? t('scraping_request.statuses.' + scrapingRequestStatus.state) : "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <b>{t('request_probe.created_at')}</b>
                        <div>{scrapingRequestStatus && scrapingRequestStatus.created_at ? toDateTimeString(scrapingRequestStatus.created_at) : "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <b>{t('job.start_at')}</b>
                        <div>{scrapingRequestStatus && scrapingRequestStatus.started_at ? toDateTimeString(scrapingRequestStatus.started_at) : "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <b>{t('job.completed_at')}</b>
                        <div>{scrapingRequestStatus && scrapingRequestStatus.completed_at ? toDateTimeString(scrapingRequestStatus.completed_at) : "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <b>{t('request_probe.url')}</b>
                        <div style={{wordBreak: "break-word"}}>{scrapingRequestStatus && scrapingRequestStatus.url ? <a href={scrapingRequestStatus.url} target="_blank" rel="noopener noreferrer">{scrapingRequestStatus.url}</a> : "-"}</div>
                    </div>
                    {scrapingRequestStatus &&
                        <div className="p-col-12">
                            <b>{t('request_probe.scraping_request_details')}</b>
                            <DataTable value={objectRows(scrapingRequestStatus)} responsive={true} style={{marginTop: ".5em"}}>
                                <Column field="key" header={t('table_headers.name')} style={{width: "14em"}}/>
                                <Column header={t('table_headers.value')} body={scrapingRequestValueTemplate}/>
                            </DataTable>
                        </div>}
                </div>}
        </div>;
    };

    const siteDefinitionTemplate = () => {
        if (siteDefinitionLoading)
            return <div style={{padding: "1em", textAlign: "center"}}><Loading/></div>;
        if (!siteDefinitionData)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;
        return <div>
            <b>{t('request_probe.site_definition')}</b>
            <DataTable value={objectRows(siteDefinitionData)} responsive={true} style={{marginTop: ".5em"}}>
                <Column field="key" header={t('table_headers.name')} style={{width: "18em"}}/>
                <Column header={t('table_headers.value')} body={(row) => valueTemplate(row.value)}/>
            </DataTable>
        </div>;
    };

    const siteDefinitionTabPanel = () => {
        return <TabPanel key="site_definition" header={iconTabHeader('pi pi-database', t('request_probe.site_definition'))}>
            {siteDefinitionTemplate()}
        </TabPanel>;
    };

    const siteSearchAnalysisTemplate = () => {
        const analysis = selectedProbe && selectedProbe.site_search_analysis;
        if (!analysis)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;
        return <div style={{marginTop: ".5em"}}>
            <div style={{marginBottom: ".4em"}}>
                <b>{t('request_probe.detected_template', 'Tespit edilen arama şablonu')}:</b>{' '}
                <code style={{wordBreak: "break-all"}}>{analysis.detected_template || '-'}</code>
                {analysis.template_guessed &&
                    <span className="p-tag p-tag-warning" style={{marginLeft: ".5em"}}>{t('request_probe.guessed', 'tahmin')}</span>}
            </div>
            <div style={{marginBottom: ".6em"}}>
                <b>{t('request_probe.best_field', 'En iyi alan')}:</b>{' '}
                {analysis.best_field
                    ? <span className="p-tag p-tag-success">{analysis.best_field}</span>
                    : '-'}
            </div>
            {analysis.proxy &&
                <div style={{marginBottom: ".6em"}}>
                    <b>{t('request_probe.proxy_used')}:</b>{' '}
                    <span className="p-tag p-tag-info">{getProxyNameLabel(analysis.proxy.name)}</span>
                    {analysis.proxy.source &&
                        <span style={{marginLeft: ".5em", color: "#6b7280"}}>({analysis.proxy.source})</span>}
                </div>}
            <DataTable value={analysis.fields || []} responsiveLayout="scroll">
                <Column field="field" header={t('request_probe.field', 'Alan')}/>
                <Column field="query" header={t('request_probe.query', 'Sorgu')}/>
                <Column field="result_count" header={t('request_probe.result_count', 'Sonuç')} style={{width: "7em"}}/>
                <Column field="top_match" header={t('request_probe.match', 'Eşleşme')} style={{width: "9em"}}/>
                <Column field="score" header={t('request_probe.score', 'Skor')} style={{width: "6em"}}/>
                <Column header={t('request_probe.link', 'Link')} style={{width: "6em"}}
                        body={(row) => (row.sample_links && row.sample_links[0])
                            ? <a href={row.sample_links[0]} target="_blank" rel="noopener noreferrer">{t('actions.open', 'Aç')}</a>
                            : '-'}/>
            </DataTable>
        </div>;
    };

    const siteSearchTabPanel = () => (
        <TabPanel key="site_search_analysis" header={iconTabHeader('pi pi-search', t('request_probe.site_search_analysis'))}>
            {siteSearchAnalysisTemplate()}
        </TabPanel>
    );

    const productCardTemplate = () => {
        if (probeProductLoading)
            return <div style={{padding: "1em"}}><Loading/></div>;
        if (!probeProduct)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;

        const p = probeProduct;
        // Price / stock / currency live on "my" competitor's latest price, not on
        // the product object itself.
        const competitors = p.competitors || [];
        const mine = competitors.find(c => c.is_mine || c.is_map) || {};
        const lastPrice = getLastPrice(mine.prices) || {};

        const codes = [
            ['SKU', p.sku], ['Barcode', p.barcode], ['MPN', p.mpn],
            ['UPC', p.upc], ['EAN', p.ean], ['ASIN', p.asin], ['Ref ID', p.ref_id]
        ].filter(([, v]) => v);
        const meta = [
            [t('product.brand'), p.brand],
            [t('product.category'), [p.category, p.sub_category].filter(Boolean).join(' / ')],
            [t('product.type'), p.type]
        ].filter(([, v]) => v);
        const properties = (p.properties || []).filter(prop => prop && (prop.value !== undefined && prop.value !== null && prop.value !== ""));

        const priceCell = (label, value) => (
            <div style={{display: "flex", flexDirection: "column", gap: 2, minWidth: 90}}>
                <span style={{fontSize: ".75rem", color: "#6b7280"}}>{label}</span>
                <span style={{fontWeight: 600}}>{value}</span>
            </div>
        );
        const hasPricing = lastPrice.value !== undefined || lastPrice.stock !== undefined || lastPrice.availability;

        return <div style={{display: "flex", gap: "1.25em", padding: "1em", flexWrap: "wrap"}}>
            {p.image &&
                <img src={p.image} alt={p.name || ""}
                     style={{width: 160, height: 160, objectFit: "contain", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff"}}/>}
            <div style={{flex: "1 1 320px", minWidth: 0}}>
                <div style={{marginBottom: ".5em"}}>
                    {getProductDetailLink(p.id, p.name || p.id)}
                </div>
                {meta.length > 0 &&
                    <div style={{display: "flex", gap: ".5em", flexWrap: "wrap", marginBottom: ".75em"}}>
                        {meta.map(([label, value]) => (
                            <span key={label} className="p-tag p-tag-info">{label}: {value}</span>
                        ))}
                    </div>}
                {hasPricing &&
                    <div style={{display: "flex", gap: "1.5em", flexWrap: "wrap", margin: ".25em 0 1em",
                        padding: ".6em .8em", background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 6}}>
                        {lastPrice.value !== undefined &&
                            priceCell(t('product.price'), formatCurrency(lastPrice.value, lastPrice.currency || p.currency))}
                        {lastPrice.stock !== undefined &&
                            priceCell(t('product.stock'), lastPrice.stock)}
                        {lastPrice.availability &&
                            priceCell(t('product.availability'), lastPrice.availability)}
                    </div>}
                {codes.length > 0 &&
                    <table style={{borderCollapse: "collapse"}}>
                        <tbody>
                        {codes.map(([label, value]) => (
                            <tr key={label}>
                                <td style={{padding: "2px 12px 2px 0", color: "#6b7280", whiteSpace: "nowrap"}}>{label}</td>
                                <td style={{padding: "2px 0", fontFamily: "monospace"}}>{value}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>}
                {properties.length > 0 &&
                    <div style={{marginTop: ".9em"}}>
                        <div style={{fontSize: ".8rem", color: "#6b7280", marginBottom: 4}}>
                            {t('product.properties.').replace('product.properties.', '')}
                        </div>
                        <div style={{display: "flex", gap: ".4em", flexWrap: "wrap"}}>
                            {properties.map((prop, i) => {
                                const label = t('product.properties.' + (prop.name || '').toLowerCase()).replace('product.properties.', '');
                                return <span key={prop.name || i} className="p-tag">{label}: {prop.value}</span>;
                            })}
                        </div>
                    </div>}
            </div>
        </div>;
    };

    const productTabPanel = () => (
        <TabPanel key="product" header={iconTabHeader('pi pi-box', t('request_probe.product'))}>
            {productCardTemplate()}
        </TabPanel>
    );

    const productLink = (id) => id
        ? <a key={id} href={"/#/productdetail?id=" + id} target="_blank" rel="noopener noreferrer">{id}</a>
        : null;

    const inPreparationJobsTemplate = () => {
        if (inPrepJobsLoading)
            return <div style={{padding: "1em"}}><Loading/></div>;
        const jobs = inPrepJobs || [];
        if (jobs.length === 0)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;

        return <div style={{marginTop: ".5em"}}>
            <div style={{marginBottom: ".6em", color: "#6b7280"}}>
                {t('request_probe.in_preparation_jobs_hint')}
            </div>
            <DataTable value={jobs} responsiveLayout="scroll" paginator={jobs.length > 15} rows={15}>
                <Column header={t('request_probe.product')} body={(row) => {
                    const ids = (row.product_ids && row.product_ids.length > 0)
                        ? row.product_ids
                        : (row.product_id ? [row.product_id] : []);
                    if (ids.length === 0)
                        return "-";
                    return <span style={{display: "flex", flexDirection: "column", gap: 2, fontFamily: "monospace"}}>
                        {ids.map(id => productLink(id))}
                    </span>;
                }}/>
                <Column field="request_type" header={t('request_probe.request_type')} style={{width: "10em"}}/>
                <Column field="state" header={t('request_probe.state')} style={{width: "9em"}}
                        body={(row) => row.state ? <span className="p-tag p-tag-info">{row.state}</span> : "-"}/>
                <Column header={t('request_probe.url')} body={(row) => row.url
                    ? <a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a> : "-"}/>
                <Column field="created_at" header={t('request_probe.created_at')} style={{width: "12em"}}
                        body={(row) => row.created_at ? toDateTimeString(row.created_at) : "-"}/>
            </DataTable>
        </div>;
    };

    const inPreparationTabPanel = () => (
        <TabPanel key="in_preparation" header={iconTabHeader('pi pi-clock', t('request_probe.in_preparation_jobs'))}>
            {inPreparationJobsTemplate()}
        </TabPanel>
    );

    // Tab bar. When embedded (onboarding prepare step) labels are hidden and only
    // icons show (with hover tooltips), to keep the embedded panel compact.
    const renderTabView = (panels) => (
        <div className={"rp-compact-tabs" + (embedded ? " rp-tabs-icononly" : "")}>
            <style>{`
                .rp-compact-tabs .p-tabview-nav { flex-wrap: wrap; }
                .rp-compact-tabs .p-tabview-nav li a { padding: .55rem .85rem; }
                .rp-compact-tabs .p-tabview-nav li a .p-tabview-title { margin: 0; line-height: 1; }
                .rp-tabs-icononly .rp-tab-label { display: none; }
            `}</style>
            <Tooltip target=".rp-compact-tabs .p-tabview-nav i[data-pr-tooltip]" position="bottom"/>
            <TabView activeIndex={detailTabIndex} onTabChange={(e) => setDetailTabIndex(e.index)}>{panels}</TabView>
        </div>
    );

    const productUrlTabsTemplate = () => {
        const panels = [
            <TabPanel key="spiders" header={iconTabHeader('pi pi-chart-bar', t('request_probe.spiders'))}>
                {spidersTemplate()}
            </TabPanel>,
            <TabPanel key="html_viewer" header={iconTabHeader('pi pi-eye', t('request_probe.html_viewer'))}>
                {htmlViewerTemplate()}
            </TabPanel>,
            <TabPanel key="response_body" header={iconTabHeader('pi pi-code', t('request_probe.response_body'))}>
                {responseBodyTemplate()}
            </TabPanel>
        ];
        if (selectedProbe && selectedProbe.request_type === "site_search" && selectedProbe.site_search_analysis)
            panels.unshift(siteSearchTabPanel());
        if (selectedProbeIsSiteDefined)
            panels.push(siteDefinitionTabPanel());
        if (selectedProbeProductId)
            panels.push(productTabPanel());
        panels.push(
            <TabPanel key="scraping_request" header={iconTabHeader('pi pi-list', t('request_probe.scraping_request'))}>
                {scrapingRequestStatusTemplate()}
            </TabPanel>);
        panels.push(inPreparationTabPanel());
        panels.push(
            <TabPanel key="proxy_attempts" header={iconTabHeader('pi pi-sitemap', t('request_probe.proxy_attempts'))}>
                {proxyAttemptsTemplate()}
            </TabPanel>);
        return renderTabView(panels);
    };

    const productListTabsTemplate = () => {
        const panels = [
            <TabPanel key="product_list_probe" header={iconTabHeader('pi pi-list', t('request_probe.product_list_probe'))}>
                {productListProbeTemplate()}
            </TabPanel>,
            <TabPanel key="html_viewer" header={iconTabHeader('pi pi-eye', t('request_probe.html_viewer'))}>
                {htmlViewerTemplate()}
            </TabPanel>,
            <TabPanel key="response_body" header={iconTabHeader('pi pi-code', t('request_probe.response_body'))}>
                {responseBodyTemplate()}
            </TabPanel>
        ];
        if (selectedProbeIsSiteDefined)
            panels.push(siteDefinitionTabPanel());
        if (selectedProbeProductId)
            panels.push(productTabPanel());
        panels.push(
            <TabPanel key="scraping_request" header={iconTabHeader('pi pi-list', t('request_probe.scraping_request'))}>
                {scrapingRequestStatusTemplate()}
            </TabPanel>);
        panels.push(inPreparationTabPanel());
        panels.push(
            <TabPanel key="proxy_attempts" header={iconTabHeader('pi pi-sitemap', t('request_probe.proxy_attempts'))}>
                {proxyAttemptsTemplate()}
            </TabPanel>);
        return renderTabView(panels);
    };

    const detailPanel = () => {
        if (!selectedProbe)
            return <div style={{padding: "2em", textAlign: "center", color: "#6b7280"}}>
                {t('actions.empty_message')}
            </div>;
        return <div style={{border: "1px solid #e5e7eb", padding: "1em", minHeight: "42em"}}>
            <div className="p-grid">
                <div className="p-col-12" style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1em", flexWrap: "wrap"}}>
                    <div style={{display: "flex", alignItems: "baseline", gap: ".5em", flex: "1 1 320px", minWidth: 0}}>
                        <b style={{fontSize: "1.1em", whiteSpace: "nowrap"}}>{selectedProbe.website || "-"}</b>
                        {selectedProbe.url &&
                            <a href={selectedProbe.url} target="_blank" rel="noopener noreferrer"
                               title={selectedProbe.url}
                               style={{
                                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                   minWidth: 0, flex: "1 1 auto", color: "#3b82f6", fontSize: ".9em"
                               }}>
                                {selectedProbe.url}
                            </a>}
                    </div>
                    <div style={{display: "flex", alignItems: "center", gap: ".5em", flexWrap: "wrap"}}>
                        <span className="p-tag p-tag-info">{selectedProbe.request_type || "-"}</span>
                        {selectedProbe.product_id &&
                            <a href={"/#/productdetail?id=" + selectedProbe.product_id +
                                     (selectedProbe.url ? "&highlight_competitor_url=" + encodeURIComponent(selectedProbe.url) : "")}
                               target="_blank" rel="noopener noreferrer"
                               style={{display: "inline-flex", alignItems: "center", gap: ".3em", color: "#3b82f6", fontSize: ".9em"}}>
                                <i className="pi pi-box"/>{t('request_probe.product')}
                            </a>}
                        {selectedProbe.is_archived &&
                            <span className="p-tag p-tag-warning">{t('request_probe.archived')}</span>}
                        <Button icon="pi pi-sync"
                                className="p-button-rounded p-button-outlined p-button-sm"
                                tooltip={t('actions.refresh', 'Yenile')}
                                tooltipOptions={{position: 'bottom'}}
                                loading={detailLoading}
                                disabled={detailLoading}
                                onClick={refreshSelectedProbe}/>
                        <Button icon="pi pi-comments"
                                className="p-button-rounded p-button-outlined p-button-help p-button-sm"
                                tooltip={t('request_probe.build_prompt')}
                                tooltipOptions={{position: 'bottom'}}
                                onClick={buildPrompt}/>
                        {selectedProbeIsSiteDefined && siteDefinitionData &&
                            <Button icon="pi pi-pencil"
                                    className="p-button-rounded p-button-outlined p-button-sm"
                                    tooltip={t('site_definitions.update')}
                                    tooltipOptions={{position: 'bottom'}}
                                    onClick={openUpdateSiteDefinition}/>}
                        <Button icon={selectedProbe.is_archived ? "pi pi-inbox" : "pi pi-folder"}
                                className="p-button-rounded p-button-outlined p-button-warning p-button-sm"
                                tooltip={selectedProbe.is_archived ? t('request_probe.unarchive') : t('request_probe.archive')}
                                tooltipOptions={{position: 'bottom'}}
                                loading={archiveLoading}
                                disabled={archiveLoading}
                                onClick={archiveSelectedProbe}/>
                        <Button icon="pi pi-trash"
                                className="p-button-rounded p-button-outlined p-button-danger p-button-sm"
                                tooltip={t('actions.remove')}
                                tooltipOptions={{position: 'bottom'}}
                                loading={deleteLoading}
                                disabled={deleteLoading}
                                onClick={() => setShowDeleteDialog(true)}/>
                        {selectedProbe.is_site_defined &&
                            <Button icon="pi pi-history"
                                    className="p-button-rounded p-button-outlined p-button-help p-button-sm"
                                    tooltip="Rerun In-Preparation Scraping Jobs in 90 days"
                                    tooltipOptions={{position: 'bottom'}}
                                    loading={rerunInPreparationLoading}
                                    disabled={rerunInPreparationLoading}
                                    onClick={rerunInPreparationJobs}/>}
                        <Button icon="pi pi-refresh"
                                className="p-button-rounded p-button-outlined p-button-sm"
                                tooltip={t('request_probe.rerun_probe')}
                                tooltipOptions={{position: 'bottom'}}
                                disabled={!selectedProbe.url || !selectedProbe.request_type}
                                onClick={rerunProbe}/>
                    </div>
                </div>
                {detailLoading &&
                    <div className="p-col-12">
                        <div style={{padding: "1em", textAlign: "center", border: "1px solid #e5e7eb"}}>
                            <Loading/>
                            <div style={{marginTop: ".5em"}}>{t('request_probe.loading_detail')}</div>
                        </div>
                    </div>}
                {!detailLoading &&
                    <React.Fragment>
                        <div className="p-col-12">{responseGroupsTemplate()}</div>
                        {getResponseGroups().length === 0 &&
                            <React.Fragment>
                                {selectedProbeIsProductList &&
                                    <div className="p-col-12">{productListProbeTemplate()}</div>}
                                <div className="p-col-12">{scrapingRequestStatusTemplate()}</div>
                            </React.Fragment>}
                        {getResponseGroups().length > 0 &&
                            <div className="p-col-12">
                                {selectedProbeIsProductList ? productListTabsTemplate() : productUrlTabsTemplate()}
                            </div>}
                    </React.Fragment>}
            </div>
        </div>;
    };

    const probeListTemplate = () => {
        if (loading)
            return <Loading/>;
        if (!items || items.length === 0)
            return <div style={{padding: "1em", color: "#6b7280"}}>{t('actions.empty_message')}</div>;

        return <div style={{border: "1px solid #e5e7eb", maxHeight: "48em", overflow: "auto"}}>
            {items.map((probe, index) => {
                const selected = selectedProbe && selectedProbe.id === probe.id;
                return <button key={probe.id || index}
                               type="button"
                               onClick={() => selectProbe(probe)}
                               style={{
                                   width: "100%",
                                   display: "block",
                                   textAlign: "left",
                                   padding: ".75em",
                                   border: "0",
                                   borderBottom: "1px solid #e5e7eb",
                                   background: selected ? "#e8f4ff" : "#fff",
                                   color: "#374151",
                                   cursor: "pointer"
                               }}>
                    {probe.website || "-"} ({probe.request_type || "-"})
                </button>;
            })}
        </div>;
    };

    const closeCreateDialog = () => {
        setShowCreateDialog(false);
        setNewProbeIsRerun(false);
    };

    const createProbeDialog = () => {
        return <Dialog header={newProbeIsRerun ? t('request_probe.rerun_probe') : t('request_probe.create_new')}
                       visible={showCreateDialog}
                       style={{maxWidth: "42em", width: "90vw"}}
                       modal={true}
                       onHide={() => !newProbeLoading && closeCreateDialog()}
                       footer={<div>
                           <Button label={t('actions.cancel')}
                                   icon="pi pi-times"
                                   className="p-button-text"
                                   disabled={newProbeLoading}
                                   onClick={closeCreateDialog}/>
                           <Button label={newProbeLoading ? t('request_probe.creating') : (newProbeIsRerun ? t('request_probe.rerun_probe') : t('request_probe.probe'))}
                                   icon="pi pi-send"
                                   disabled={!newProbeUrl.trim() || newProbeLoading}
                                   loading={newProbeLoading}
                                   onClick={createProbe}/>
                       </div>}>
            <div className="p-fluid">
                <div className="p-field">
                    <label htmlFor="probe_create_url">{t('request_probe.url')}</label>
                    <InputText id="probe_create_url"
                               value={newProbeUrl}
                               onChange={(e) => setNewProbeUrl(e.target.value)}
                               placeholder="https://example.com/product"
                               autoFocus={true}/>
                </div>
                <div className="p-field">
                    <label htmlFor="probe_create_type">{t('request_probe.request_type')}</label>
                    <Dropdown id="probe_create_type"
                              value={newProbeRequestType}
                              options={createRequestTypeOptions}
                              onChange={(e) => setNewProbeRequestType(e.value)}/>
                </div>
                <div className="p-field">
                    <label htmlFor="probe_create_proxy">{t('request_probe.proxy_country_optional')}</label>
                    <Dropdown id="probe_create_proxy"
                              value={newProbeProxyCountry}
                              options={proxyCountryOptions}
                              onChange={(e) => setNewProbeProxyCountry(e.value)}/>
                </div>
                <div className="p-field">
                    <label htmlFor="probe_create_selected_proxies">{t('request_probe.selected_proxies')}</label>
                    <MultiSelect id="probe_create_selected_proxies"
                                 value={newProbeSelectedProxies}
                                 options={probeProxyOptions}
                                 onChange={(e) => setNewProbeSelectedProxies(e.value || [])}
                                 filter={true}
                                 maxSelectedLabels={2}/>
                </div>
                <div className="p-field">
                    <label htmlFor="probe_create_selected_base_spiders">{t('request_probe.selected_base_spiders')}</label>
                    <MultiSelect id="probe_create_selected_base_spiders"
                                 value={newProbeSelectedBaseSpiders}
                                 options={probeBaseSpiderOptions}
                                 onChange={(e) => setNewProbeSelectedBaseSpiders(e.value || [])}
                                 filter={true}
                                 maxSelectedLabels={2}/>
                </div>
                {newProbeRequestType === 'site_search' && <>
                    <div className="p-field">
                        <label htmlFor="probe_create_search_template">{t('request_probe.search_url_template')}</label>
                        <InputText id="probe_create_search_template"
                                   value={newProbeSearchUrlTemplate}
                                   onChange={(e) => setNewProbeSearchUrlTemplate(e.target.value)}
                                   placeholder="https://site.com/search?q={q}"/>
                    </div>
                    <div className="p-field">
                        <label htmlFor="probe_create_search_keyword">{t('request_probe.search_keyword')}</label>
                        <InputText id="probe_create_search_keyword"
                                   value={newProbeSearchKeyword}
                                   onChange={(e) => setNewProbeSearchKeyword(e.target.value)}/>
                    </div>
                    <div className="p-field">
                        <label htmlFor="probe_create_product_id">{t('request_probe.reference_product_id')}</label>
                        <InputText id="probe_create_product_id"
                                   value={newProbeProductId || ""}
                                   onChange={(e) => setNewProbeProductId(e.target.value || null)}/>
                    </div>
                </>}
            </div>
        </Dialog>;
    };

    const siteDefinitionDialog = () => {
        if (!showSiteDefinitionDialog || !siteDefinitionSeed)
            return null;
        const isEdit = !!siteDefinitionSeed.id;
        return <Dialog header={isEdit ? t('site_definitions.update') : t('site_definitions.add')}
                       visible={true}
                       style={{maxWidth: '50vw', minWidth: '20vw'}}
                       modal={true}
                       onHide={() => setShowSiteDefinitionDialog(false)}>
            <SiteDefinitionAdd selectedDefinition={siteDefinitionSeed}
                               siteDefinitions={siteDefinitionData ? [siteDefinitionData] : []}
                               onComplete={onSiteDefinitionComplete}/>
        </Dialog>;
    };

    const deleteDialog = () => {
        return <Dialog header={t('request_probe.confirm_delete')}
                       visible={showDeleteDialog}
                       style={{maxWidth: "30em", width: "90vw"}}
                       modal={true}
                       onHide={() => !deleteLoading && setShowDeleteDialog(false)}
                       footer={<div>
                           <Button label={t('yes_no.no')}
                                   icon="pi pi-times"
                                   className="p-button-text"
                                   disabled={deleteLoading}
                                   onClick={() => setShowDeleteDialog(false)}/>
                           <Button label={t('yes_no.yes')}
                                   icon="pi pi-check"
                                   className="p-button-danger"
                                   loading={deleteLoading}
                                   disabled={deleteLoading}
                                   onClick={deleteSelectedProbe}/>
                       </div>}>
            <div style={{wordBreak: "break-word"}}>
                {t('request_probe.confirm_delete_msg')}
                {selectedProbe && <div style={{marginTop: ".5em"}}><b>{selectedProbe.website || "-"}</b> ({selectedProbe.request_type || "-"})</div>}
            </div>
        </Dialog>;
    };

    const buildPromptDialog = () => {
        return <Dialog header={t('request_probe.build_prompt')}
                       visible={showPromptDialog}
                       style={{maxWidth: "52em", width: "90vw"}}
                       modal={true}
                       onHide={() => setShowPromptDialog(false)}
                       footer={<div>
                           <Button label={t('actions.close')}
                                   icon="pi pi-times"
                                   className="p-button-text"
                                   onClick={() => setShowPromptDialog(false)}/>
                           <Button label={t('actions.copy')}
                                   icon="pi pi-copy"
                                   disabled={!promptText}
                                   onClick={copyPrompt}/>
                       </div>}>
            <div className="p-fluid">
                <InputTextarea value={promptText}
                               onChange={(e) => setPromptText(e.target.value)}
                               rows={20}
                               autoResize={false}
                               style={{width: "100%", fontFamily: "monospace"}}/>
            </div>
        </Dialog>;
    };

    const scenarioDialog = () => {
        const showFields = scenarioUsesFields(scenario);
        return <Dialog header={t('request_probe.build_prompt_scenario_title')}
                       visible={showScenarioDialog}
                       style={{maxWidth: "40em", width: "90vw"}}
                       modal={true}
                       onHide={() => !scenarioLoading && setShowScenarioDialog(false)}
                       footer={<div>
                           <Button label={t('actions.cancel')}
                                   icon="pi pi-times"
                                   className="p-button-text"
                                   disabled={scenarioLoading}
                                   onClick={() => setShowScenarioDialog(false)}/>
                           <Button label={scenarioLoading ? t('request_probe.creating') : t('request_probe.generate')}
                                   icon="pi pi-comments"
                                   loading={scenarioLoading}
                                   onClick={generatePrompt}/>
                       </div>}>
            <div className="p-fluid">
                <div className="p-field">
                    <label htmlFor="scenario_select">{t('request_probe.scenario_label')}</label>
                    <Dropdown id="scenario_select" value={scenario} options={scenarioOptions()}
                              onChange={(e) => setScenario(e.value)}/>
                </div>
                {showFields &&
                    <div className="p-field">
                        <label htmlFor="scenario_fields_select">{t('request_probe.scenario_fields')}</label>
                        <MultiSelect id="scenario_fields_select" value={scenarioFields}
                                     options={scenarioFieldOptions()}
                                     onChange={(e) => setScenarioFields(e.value || [])}
                                     filter={true} display="chip"/>
                        <small style={{color: "#6b7280"}}>{t('request_probe.scenario_fields_hint')}</small>
                    </div>}
            </div>
        </Dialog>;
    };

    return <React.Fragment>
        {createProbeDialog()}
        {siteDefinitionDialog()}
        {deleteDialog()}
        {buildPromptDialog()}
        {scenarioDialog()}
        {spiderDetailDialog()}
        <div className="p-grid" style={{alignItems: "flex-start"}}>
            <div className="p-col-12 p-md-3">
                <Button label={t('request_probe.create_new')}
                        icon="pi pi-plus"
                        onClick={() => setShowCreateDialog(true)}
                        style={{width: "100%", marginBottom: "1em"}}/>
                <div style={{border: "1px solid #e5e7eb", padding: "1em", marginBottom: "1em"}}>
                    <div style={{marginBottom: ".75em"}}>
                        <label htmlFor="probe_filter_type">{t('request_probe.request_type')}</label>
                        <Dropdown id="probe_filter_type" value={filters.request_type} options={requestTypeOptions}
                                  onChange={(e) => setFilters({...filters, request_type: e.value})}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div style={{marginBottom: ".75em"}}>
                        <label htmlFor="probe_filter_website">{t('request_probe.website')}</label>
                        <InputText id="probe_filter_website" value={filters.website}
                                   onChange={(e) => setFilters({...filters, website: e.target.value})}
                                   placeholder="example.com" style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div style={{marginBottom: ".75em"}}>
                        <label htmlFor="probe_filter_archive_status">{t('request_probe.archive_status')}</label>
                        <Dropdown id="probe_filter_archive_status" value={filters.archive_status} options={archiveStatusOptions}
                                  onChange={(e) => setFilters({...filters, archive_status: e.value})}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div style={{marginBottom: ".75em"}}>
                        <label htmlFor="probe_filter_limit">Limit</label>
                        <Dropdown id="probe_filter_limit" value={filters.limit} options={limitOptions}
                                  onChange={(e) => setFilters({...filters, limit: e.value})}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <Button label={t('actions.refresh')} icon="pi pi-refresh" className="p-button-outlined" onClick={load} style={{width: "100%"}}/>
                </div>
                {probeListTemplate()}
            </div>
            <div className="p-col-12 p-md-9">
                {detailPanel()}
            </div>
        </div>
    </React.Fragment>;
};

export default RequestProbes;
